import { NavLink } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useBugReports } from '../../../context/BugReportContext.jsx';
import { LogOut, User, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';
import logoIcon from '../../../assets/icons/ep256.png';
import { FRONTEND_BUILD, FRONTEND_VERSION, RELEASES } from '@shared/appVersion.js';
import './Sidebar.css';

// Baked into the bundle at build time, so this is the build this tab is
// actually running - not whatever the server has now. See shared/appVersion.js.
const buildRelease = RELEASES.find((release) => release.build === FRONTEND_BUILD);

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { openCount, unreadCount } = useBugReports();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📈' },
    { path: '/dashboard/create', label: 'Deck List', icon: '📚' },
    { path: '/dashboard/news', label: 'Short News', icon: '🧾' },
    ...(isAdmin ? [{ path: '/dashboard/users', label: 'Users', icon: '👥' }] : []),
    {
      path: '/dashboard/bug-reports',
      label: 'Report Bug',
      icon: '🐞',
      // Unread replies win over the admin open-bugs count - they need an answer
      badge: unreadCount > 0 ? unreadCount : isAdmin && openCount > 0 ? openCount : null,
      badgeKind: unreadCount > 0 ? 'unread' : 'open',
    },
    { path: '/dashboard/settings', label: 'Dev Tool', icon: '🔧' },
  ];

  const handleLogout = async () => {
    await logout();
    if (onClose) onClose();
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  const displayName = user?.firstName || user?.email?.split('@')[0] || 'User';

  return (
    <>
      <aside className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
        {/* Top header with close button and profile */}
        <div className="sidebar-top-header">
          <div className="sidebar-profile-mini">
            <div className="profile-avatar-mini">
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={displayName}
                  className="profile-avatar-image"
                />
              ) : (
                <User size={18} />
              )}
            </div>
            <div className="profile-info-mini">
              <span className="profile-name-mini">{displayName}</span>
              <span className="profile-email-mini">{user?.email || ''}</span>
            </div>
          </div>
          {/* Close button - only shows when sidebar is open on mobile/tablet */}
          {isOpen && (
            <button
              className="sidebar-close-btn"
              onClick={onClose}
              aria-label="Close sidebar"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <div className="sidebar-logo">
          <img src={logoIcon} alt="EasyPanel Logo" className="sidebar-logo-image" />
          <div className="sidebar-brand">
            <span className="sidebar-brand-name">EasyPanel</span>
            <span className="sidebar-brand-tagline">by Inshorts</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/dashboard'}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'sidebar-link--active' : ''}`
              }
              onClick={handleNavClick}
            >
              <span className="sidebar-link-icon">{item.icon}</span>
              <span className="sidebar-link-label">{item.label}</span>
              {item.badge ? (
                <span
                  className={`sidebar-link-badge ${
                    item.badgeKind === 'unread' ? 'sidebar-link-badge--unread' : ''
                  }`}
                  title={
                    item.badgeKind === 'unread'
                      ? `${item.badge} unread ${item.badge > 1 ? 'messages' : 'message'}`
                      : `${item.badge} open bug${item.badge > 1 ? 's' : ''}`
                  }
                >
                  {item.badgeKind === 'unread'
                    ? `+${item.badge > 99 ? 99 : item.badge}`
                    : item.badge > 99
                      ? '99+'
                      : item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        {/* Profile section - desktop only */}
        <div className="sidebar-profile">
          <div
            className="profile-header"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            <div className="profile-avatar">
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={displayName}
                  className="profile-avatar-image"
                />
              ) : (
                <User size={20} />
              )}
            </div>
            <div className="profile-info">
              <p className="profile-name">{displayName}</p>
              <p className="profile-email">{user?.email}</p>
            </div>
            <ChevronDown
              size={16}
              className={`profile-chevron ${showProfileMenu ? 'profile-chevron--open' : ''}`}
            />
          </div>

          {/* Logout button - shows when profile is expanded */}
          {showProfileMenu && (
            <div className="profile-menu-desktop">
              <button className="profile-menu-item" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>

        {/* Logout button at bottom - only for mobile/tablet */}
        <div className="sidebar-logout sidebar-logout--mobile">
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>

        {/* Which build this tab is running - the first thing to check when
            someone says "mere paas purana dikh raha hai" */}
        <div
          className="sidebar-build"
          title={`Build ${FRONTEND_BUILD}${buildRelease?.date ? ` · ${buildRelease.date}` : ''}`}
        >
          v{FRONTEND_VERSION} · build {FRONTEND_BUILD}
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={onClose}></div>
      )}
    </>
  );
};

export default Sidebar;
